import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppSession, MeView, PatientView } from './lib/types';
import { ApiError, api, loadSession, saveSession, setUnauthorizedHandler } from './lib/api';
import { SESSION_KEY } from './lib/ssoLogout';
import { startHomeSignIn, takeHomeCallback, takeMandateCallback, takeOrgCallback, takeProfileName, type AuthConfig } from './lib/home';
import { describeDemoError, type DemoPersona } from './lib/demo';
import { connectAsDemoUser, fetchDemoPersonas } from './lib/quickConnect';
import { FRONT_HASH, goTo, hasWelcomed, route, takeReturn } from './lib/routes';
import { signOutTo, type SignOutReason } from './lib/session';
import { useHash } from './lib/hooks';
import { SignInPage } from './pages/SignInPage';
import { PatientApp } from './pages/PatientApp';
import { PracticeApp } from './pages/PracticeApp';
import { JoinPage } from './pages/JoinPage';
import { EmailLinkPage, InvitePage } from './pages/InvitePage';

/** Everything sign-in related, in one bag, so every surface can render every state of it. */
export interface AuthState {
  config: AuthConfig | null;
  configError: string | null;
  busy: boolean;
  error: string | null;
  signInWithHome: (name?: string) => void;
  dismissError: () => void;
  personas: DemoPersona[];
  demoBusy: string | null;
  demoError: string | null;
  connectAsDemo: (handle: string) => void;
  notice: string | null;
}

export function App() {
  const hash = useHash();
  const [session, setSession] = useState<AppSession | null>(() => loadSession());
  const [me, setMe] = useState<MeView | null>(null);
  const [meError, setMeError] = useState<string | null>(null);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [personas, setPersonas] = useState<DemoPersona[]>([]);
  const [demoBusy, setDemoBusy] = useState<string | null>(null);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [orgReturn, setOrgReturn] = useState<{ code: string; state: string } | null>(null);

  const sessionRef = useRef(session);
  sessionRef.current = session;

  const login = useCallback((s: AppSession) => {
    saveSession(s);
    setSession(s);
    setMe(null);
    setError(null);
    setDemoError(null);
    setNotice(null);
  }, []);

  const endSession = useCallback((reason: SignOutReason) => {
    const outcome = signOutTo(reason);
    const current = sessionRef.current;
    if (outcome.revoke && current) void api.signOut(current.token);
    saveSession(outcome.session);
    setSession(outcome.session);
    setMe(null);
    setNotice(outcome.notice);
    setError(null);
    setDemoError(null);
    goTo(outcome.hash);
  }, []);

  const signOut = useCallback(() => endSession('user'), [endSession]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SESSION_KEY && e.key !== null) return;
      if (e.newValue) return;
      if (sessionRef.current) endSession('elsewhere');
    };
    addEventListener('storage', onStorage);
    return () => removeEventListener('storage', onStorage);
  }, [endSession]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (sessionRef.current) endSession('expired');
    });
    return () => setUnauthorizedHandler(null);
  }, [endSession]);

  useEffect(() => {
    let alive = true;
    api
      .authConfig()
      .then((c) => alive && (setConfig(c), setConfigError(null)))
      .catch((e: unknown) => alive && setConfigError(e instanceof ApiError ? `${e.status}: ${e.message}` : 'Could not reach OncoCoach.'));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!config || session) return;
    let alive = true;
    void fetchDemoPersonas(config).then((ps) => alive && setPersonas(ps));
    return () => {
      alive = false;
    };
  }, [config, session]);

  /** Who this person is HERE, re-read whenever the session changes or a page asks for it. */
  const refreshMe = useCallback(async () => {
    const current = sessionRef.current;
    if (!current) return;
    try {
      setMe(await api.me(current.token));
      setMeError(null);
    } catch (e) {
      setMeError(e instanceof ApiError ? e.message : 'Could not read who you are here.');
    }
  }, []);

  useEffect(() => {
    if (session) void refreshMe();
  }, [session, refreshMe]);

  // Return legs: a redeem-mandate ceremony and an org-create ceremony land on the same redirect URI
  // as a sign-in and are told apart by their own stashed state. They must be consumed first.
  useEffect(() => {
    const mandate = takeMandateCallback();
    if (mandate.status === 'signed-in') {
      const current = sessionRef.current;
      if (!current) {
        setError('Your Home finished the authorisation, but this browser is no longer signed in — sign in and try again.');
        return;
      }
      setBusy(true);
      api
        .homeMandate({ code: mandate.code, codeVerifier: mandate.codeVerifier, authOrigin: mandate.authOrigin, nonce: mandate.nonce, state: mandate.state }, current.token)
        .then(() => {
          setNotice('Redemptions are authorised.');
          const back = takeReturn();
          if (back) goTo(back);
        })
        .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not record the redeem authorisation.'))
        .finally(() => setBusy(false));
      return;
    }
    if (mandate.status === 'error') setError(mandate.message);
    const org = takeOrgCallback();
    if (org.status === 'signed-in') {
      setOrgReturn({ code: org.code, state: org.state });
      goTo('#/practice/setup');
    } else if (org.status === 'error') setError(org.message);
  }, []);

  useEffect(() => {
    const outcome = takeHomeCallback();
    if (outcome.status === 'none') return;
    if (outcome.status === 'error') {
      setError(outcome.message);
      return;
    }
    setBusy(true);
    const profileName = takeProfileName();
    api
      .homeLogin({ code: outcome.code, codeVerifier: outcome.codeVerifier, authOrigin: outcome.authOrigin, nonce: outcome.nonce, state: outcome.state, ...(profileName ? { profileName } : {}) })
      .then((r) => login({ token: r.token, personId: r.personId, name: r.name, via: 'home', address: r.address, agentName: r.agentName }))
      .catch((e: unknown) => setError(e instanceof ApiError ? `Your Home signed you in, but OncoCoach would not accept it — ${e.message}` : 'Could not finish signing in.'))
      .finally(() => setBusy(false));
  }, [login]);

  const signInWithHome = useCallback(
    (name?: string) => {
      if (!config) return;
      setBusy(true);
      setError(null);
      startHomeSignIn(config, name ?? '')
        .then((url) => {
          location.href = url;
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
          setBusy(false);
        });
    },
    [config],
  );

  const connectAsDemo = useCallback(
    (handle: string) => {
      if (!config || demoBusy) return;
      setDemoBusy(handle);
      setDemoError(null);
      setError(null);
      connectAsDemoUser(config, handle)
        .then(login)
        .catch((e: unknown) => setDemoError(describeDemoError(e)))
        .finally(() => setDemoBusy(null));
    },
    [config, demoBusy, login],
  );

  const auth: AuthState = { config, configError, busy, error, signInWithHome, dismissError: () => setError(null), personas, demoBusy, demoError, connectAsDemo, notice };
  const r = route(hash);

  useEffect(() => {
    if (session && r.page === 'signin') goTo(FRONT_HASH);
  }, [session, r.page]);

  // An invitation and an emailed sign-in link both work signed out, and both end in a session.
  if (r.page === 'invite') {
    return (
      <InvitePage
        token={r.token}
        session={session}
        onLogin={(s) => {
          login(s);
          goTo('#/welcome');
        }}
      />
    );
  }
  if (r.page === 'signin-link') {
    return (
      <EmailLinkPage
        token={r.token}
        onLogin={(s) => {
          login(s);
          goTo(FRONT_HASH);
        }}
      />
    );
  }
  if (!session || r.page === 'signin') return <SignInPage auth={auth} onLogin={login} />;

  if (!me) {
    return (
      <div className="phone">
        <div className="loading">{meError ? <p className="form-error">{meError}</p> : <p className="hint">Opening OncoCoach…</p>}</div>
      </div>
    );
  }

  if (me.role === 'patient') {
    const welcome = r.page === 'welcome' || (r.page === 'front' && !hasWelcomed(session.personId));
    return <PatientApp session={session} me={me} config={config} route={r} welcome={welcome} onSignOut={signOut} notice={notice} clearNotice={() => setNotice(null)} />;
  }
  if (me.role === 'doctor' || me.role === 'manager') {
    return <PracticeApp session={session} me={me} config={config} route={r} onSignOut={signOut} orgReturn={orgReturn} clearOrgReturn={() => setOrgReturn(null)} onChanged={refreshMe} />;
  }
  return <JoinPage session={session} me={me} config={config} route={r} onSignOut={signOut} onChanged={refreshMe} orgReturn={orgReturn} clearOrgReturn={() => setOrgReturn(null)} />;
}

/** Shared helper for pages that poll the patient view. */
export function usePatientView(token: string, intervalMs = 15_000): { view: PatientView | null; error: string | null; reload: () => Promise<void> } {
  const [view, setView] = useState<PatientView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      setView(await api.patient(token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load your dashboard.');
    }
  }, [token]);
  useEffect(() => {
    void reload();
    const h = setInterval(() => {
      if (!document.hidden) void reload();
    }, intervalMs);
    return () => clearInterval(h);
  }, [reload, intervalMs]);
  return { view, error, reload };
}
